import React, {
  ChangeEvent, useRef, useState,
} from 'react';
import {
  Button, Modal, Row,
} from 'react-bootstrap';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import {
  GetBlobOptions,
  ImageCropper,
  ImageCropperProps,
  useImageCropper,
} from '../../../../../dorian-shared/components/ImageCropper/ImageCropper';
import { imageFileToDataURL } from '../../../../../dorian-shared/components/ImageCropper/imageFileToDataURL';
import { useToggle } from '../../../../../dorian-shared/hooks/useToggle';
import { createFileNameFromImageURL } from './createFileNameFromImageURL';
import classes from './ImageInput.module.scss';

export interface FileInputValue {
  fileName: string,
  file: Blob
}

interface ImageInputProps {
  initialURL: string
  onConfirm: (fileInputValue: FileInputValue, imageURL: string) => void
  error?: string
  imageCropperProps?: Partial<ImageCropperProps>
  getBlobOptions?: GetBlobOptions
  accept?: string
  label?: string
  labelBadge?: React.ReactNode
  /**
   * Controls how the post-upload thumbnail is rendered.
   *
   * 'full' (default) — large rectangular preview; used for the legacy
   *   landscape cover. No placeholder is shown when the field is empty.
   *
   * 'oval' — clips the thumbnail to a 21:23.5 ellipse matching EventOval in
   *   dorian-lib-creation-tools-ui EventShelfCard. Used for the card icon. An oval placeholder is
   *   always rendered so the file input never shifts position.
   *
   * 'compact' — fixed 9:16 portrait thumbnail. Used for the modal cover.
   *   A portrait placeholder is always rendered so the file input never shifts.
   */
  previewVariant?: 'oval' | 'compact' | 'full'
  /**
   * When provided, a ✕ button is rendered overlaid on the image preview.
   * Clicking it calls this callback (typically to open a confirmation dialog).
   * Only shown when initialURL is set and no new upload is pending — the
   * parent controls visibility by passing undefined when remove is unavailable.
   */
  onRemoveRequest?: () => void
}

export function ImageInput(props: ImageInputProps) {
  const {
    initialURL,
    onConfirm,
    error,
    imageCropperProps,
    getBlobOptions,
    accept,
    label = 'Cover',
    labelBadge,
    previewVariant = 'full',
    onRemoveRequest,
  } = props;

  const [isCropperModalShown, showCropperModal, hideCropperModal] = useToggle();
  const [isModalConfirmDisabled, disableConfirmButton, enableConfirmButton] = useToggle();

  const [fileInputValue, setFileInputValue] = useState<FileInputValue>();
  const [imageURLToCrop, setImageURLToCrop] = useState(initialURL);
  const [cropError, setCropError] = useState<string | null>(null);
  const handleImageChange = (newFileInputValue: FileInputValue) => {
    setFileInputValue(newFileInputValue);
    return imageFileToDataURL(newFileInputValue.file).then(setImageURLToCrop);
  };

  const lastFileNameRef = useRef(createFileNameFromImageURL(imageURLToCrop));

  const {
    handleCropperInitialized,
    getBlob,
  } = useImageCropper();

  const handleEditClick = () => {
    setImageURLToCrop(initialURL);
    showCropperModal();
    disableConfirmButton();
    setCropError(null);
  };

  const handleCropClick = () => {
    setCropError(null);
    getBlob(getBlobOptions)
      .then((blob) => {
        if (blob) {
          handleImageChange({
            file: blob,
            fileName: lastFileNameRef.current,
          }).then(enableConfirmButton);
        } else {
          // Keep Confirm disabled — the stale uncropped file must not be uploadable.
          // The operator must retry Crop or choose a different file.
          setCropError('Could not read this image. Please try again or choose a different file.');
        }
      })
      .catch(() => {
        setCropError('Could not process this image. Please try again or choose a different file.');
      });
  };

  const handleModalConfirmClick = () => {
    if (fileInputValue && imageURLToCrop) {
      onConfirm(fileInputValue, imageURLToCrop);
      hideCropperModal();
    }
  };

  const handleNewFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files?.[0];
    const fileName = event.target.value;
    if (file) {
      lastFileNameRef.current = fileName;
      disableConfirmButton();
      setCropError(null);
      handleImageChange({
        file,
        fileName,
      }).then(showCropperModal);
    }
  };

  return (
    <>
      <Row className="mb-3">
        {(previewVariant !== 'full' || initialURL) && (
          <Col xs="auto" className={classes.imageContainer}>
            {initialURL ? (
              <>
                <div className={classes.previewWrapper}>
                  {previewVariant === 'oval' ? (
                    <div className={classes.ovalPreview}>
                      <img
                        alt="card icon preview"
                        src={initialURL}
                        className={classes.ovalImage}
                      />
                    </div>
                  ) : previewVariant === 'compact' ? (
                    <img
                      alt="event cover"
                      src={initialURL}
                      className={classes.compactImage}
                    />
                  ) : (
                    <img
                      alt="event cover"
                      src={initialURL}
                      className={classes.image}
                    />
                  )}
                  {onRemoveRequest && (
                    <button
                      type="button"
                      className={classes.removeOverlay}
                      onClick={onRemoveRequest}
                      aria-label={`Remove ${label}`}
                    >
                      <span className={classes.removeOverlayCircle} aria-hidden="true">✕</span>
                    </button>
                  )}
                </div>
                <Button
                  className={classes.button}
                  variant="primary"
                  onClick={handleEditClick}
                >
                  Edit
                </Button>
              </>
            ) : (
              /* Empty state: shape-matched placeholder so the file-input col
                 does not shift when the image eventually loads. */
              previewVariant === 'oval'
                ? <div className={classes.ovalEmpty} aria-hidden="true" />
                : <div className={classes.compactEmpty} aria-hidden="true" />
            )}
          </Col>
        )}
        <Form.Group as={Col}>
          <Form.Label
            style={labelBadge ? {
              position: 'relative',
              display: 'inline-block',
            } : undefined}
          >
            {label}
            {labelBadge && (
              <div className="position-absolute" style={{ top: '-1em', right: '-1.5em' }}>
                {labelBadge}
              </div>
            )}
          </Form.Label>
          <Form.Control
            type="file"
            name="image"
            onChange={handleNewFileUpload}
            isInvalid={!!error}
            accept={accept}
          />
          <Form.Control.Feedback
            type="invalid"
          >
            {error}
          </Form.Control.Feedback>
        </Form.Group>

      </Row>
      <Modal
        size="lg"
        show={isCropperModalShown}
        onHide={hideCropperModal}
        centered
        fullscreen="sm-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>Prepare image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <ImageCropper
              style={{ width: '100%' }}
              src={imageURLToCrop}
              onInitialized={handleCropperInitialized}
              {...imageCropperProps}
            />
          </Row>
          <Modal.Footer>
            {cropError && (
              <div className="text-danger w-100 mb-2" style={{ fontSize: '0.875rem' }}>
                {cropError}
              </div>
            )}
            <Button
              className={classes.button}
              variant="primary"
              onClick={handleCropClick}
            >
              Crop
            </Button>
            <Button
              disabled={isModalConfirmDisabled}
              className={classes.button}
              variant="primary"
              onClick={handleModalConfirmClick}
            >
              Confirm
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>

    </>
  );
}
